/**
 * Colour tones — the one set of hue families every portal colours with.
 *
 * On web a tone is a set of CSS tokens (globals.css: `text-tone-green`,
 * `bg-tone-amber-subtle`, `bg-tone-blue-solid` + `text-tone-blue-on-solid` …),
 * contrast-tested in tones.spec.ts. React Native has no CSS, so the phone reads
 * the SAME shades here as hex. Roles per theme:
 *
 *   text     text / icon colour on the page, a card or the tone's own tint
 *   solid    a fill; `onSolid` is the text on it
 *   subtle   the tone's tint (a chip, a banner, an icon box)
 *   line     a border on the tint
 *
 * Light: text 700, solid 700 + white, subtle 50, line 200.
 * Dark:  text 300, solid 400 + 950, subtle = the 500 at 16%, line 800.
 * The hex is GENERATED from Tailwind's OKLCH palette (the shades globals.css
 * resolves), clipped to sRGB as the browser paints it; tones.spec.ts proves it.
 */

export const TONES = [
  'green',
  'amber',
  'orange',
  'red',
  'rose',
  'blue',
  'cyan',
  'violet',
  'slate',
] as const;

export type Tone = (typeof TONES)[number];

export interface ToneHex {
  text: string;
  solid: string;
  onSolid: string;
  subtle: string;
  line: string;
}

export const TONE_HEX: Record<'light' | 'dark', Record<Tone, ToneHex>> = {
  light: {
    green: {
      text: '#007A55',
      solid: '#007A55',
      onSolid: '#FFFFFF',
      subtle: '#ECFDF5',
      line: '#A4F4CF',
    },
    amber: {
      text: '#BB4D00',
      solid: '#BB4D00',
      onSolid: '#FFFFFF',
      subtle: '#FFFBEB',
      line: '#FEE685',
    },
    orange: {
      text: '#CA3500',
      solid: '#CA3500',
      onSolid: '#FFFFFF',
      subtle: '#FFF7ED',
      line: '#FFD6A7',
    },
    red: {
      text: '#C10007',
      solid: '#C10007',
      onSolid: '#FFFFFF',
      subtle: '#FEF2F2',
      line: '#FFC9C9',
    },
    rose: {
      text: '#C70036',
      solid: '#C70036',
      onSolid: '#FFFFFF',
      subtle: '#FFF1F2',
      line: '#FFCCD3',
    },
    blue: {
      text: '#1447E6',
      solid: '#1447E6',
      onSolid: '#FFFFFF',
      subtle: '#EFF6FF',
      line: '#BEDBFF',
    },
    cyan: {
      text: '#007595',
      solid: '#007595',
      onSolid: '#FFFFFF',
      subtle: '#ECFEFF',
      line: '#A2F4FD',
    },
    violet: {
      text: '#7008E7',
      solid: '#7008E7',
      onSolid: '#FFFFFF',
      subtle: '#F5F3FF',
      line: '#DDD6FF',
    },
    slate: {
      text: '#314158',
      solid: '#314158',
      onSolid: '#FFFFFF',
      subtle: '#F8FAFC',
      line: '#E2E8F0',
    },
  },
  dark: {
    green: {
      text: '#5EE9B5',
      solid: '#00D492',
      onSolid: '#002C22',
      subtle: 'rgba(0,188,125,0.16)',
      line: '#006045',
    },
    amber: {
      text: '#FFD230',
      solid: '#FFB900',
      onSolid: '#461901',
      subtle: 'rgba(254,154,0,0.16)',
      line: '#973C00',
    },
    orange: {
      text: '#FFB86A',
      solid: '#FF8904',
      onSolid: '#441306',
      subtle: 'rgba(255,105,0,0.16)',
      line: '#9F2D00',
    },
    red: {
      text: '#FFA2A2',
      solid: '#FF6467',
      onSolid: '#460809',
      subtle: 'rgba(251,44,54,0.16)',
      line: '#9F0712',
    },
    rose: {
      text: '#FFA1AD',
      solid: '#FF637E',
      onSolid: '#4D0218',
      subtle: 'rgba(255,32,86,0.16)',
      line: '#A50036',
    },
    blue: {
      text: '#8EC5FF',
      solid: '#51A2FF',
      onSolid: '#162456',
      subtle: 'rgba(43,127,255,0.16)',
      line: '#193CB8',
    },
    cyan: {
      text: '#53EAFD',
      solid: '#00D3F2',
      onSolid: '#053345',
      subtle: 'rgba(0,184,219,0.16)',
      line: '#005F78',
    },
    violet: {
      text: '#C4B4FF',
      solid: '#A684FF',
      onSolid: '#2F0D68',
      subtle: 'rgba(142,81,255,0.16)',
      line: '#5D0EC0',
    },
    slate: {
      text: '#CAD5E2',
      solid: '#90A1B9',
      onSolid: '#020618',
      subtle: 'rgba(98,116,142,0.16)',
      line: '#1D293D',
    },
  },
};

/**
 * A tone's roles on the web as CSS values that follow the theme — for a style
 * that takes a colour value rather than a class (an inline style, an SVG
 * `fill` / `stroke`, a chart series). Where a class will do, use the tone's
 * Tailwind classes (`TONE_CLASSES` in @tonnie/ui).
 */
export interface ToneVars {
  /** Text / icon colour — reads on the page, a card and `subtle`. */
  text: string;
  /** A fill; `onSolid` is the text on it. */
  solid: string;
  onSolid: string;
  /** The tone's tint. */
  subtle: string;
  /** A border on the tint. */
  line: string;
}

export function toneVars(tone: Tone): ToneVars {
  return {
    text: `var(--tone-${tone})`,
    solid: `var(--tone-${tone}-solid)`,
    onSolid: `var(--tone-${tone}-on-solid)`,
    subtle: `var(--tone-${tone}-subtle)`,
    line: `var(--tone-${tone}-line)`,
  };
}

/**
 * MESH — the four CheckUp foundations and the tone each letter wears, on every
 * portal (Evidence, the talent drawer, the citizen's results, the phone).
 * Hard skills is slate, NOT red: red is the platform's severity-bad colour, and
 * "0% in red" reads as "0% bad" rather than "0% Hard skills" — the score, not
 * the hue, carries the judgement.
 */
export const MESH_LETTERS = ['M', 'E', 'S', 'H'] as const;
export type MeshLetter = (typeof MESH_LETTERS)[number];

export const MESH_TONE: Record<MeshLetter, Tone> = {
  M: 'blue',
  E: 'orange',
  S: 'green',
  H: 'slate',
};

/**
 * The iQulture badges, in ladder order, and the tone each wears on every
 * portal: the hero reveal, the history chart's tier lines, the dashboards,
 * the company banner and the phone. The SERVER awards the badge; the client
 * only colours it, from this one map.
 */
export const IQULTURE_BADGE_IDS = ['noob', 'potential', 'pro'] as const;
export type IQultureBadgeToneKey = (typeof IQULTURE_BADGE_IDS)[number];

export const IQULTURE_BADGE_TONE: Record<IQultureBadgeToneKey, Tone> = {
  noob: 'rose',
  potential: 'amber',
  pro: 'green',
};
